/**
 * CancellationSurveyModal — exit survey al cancelar suscripción.
 *
 * UX: empático, no manipulativo. El coach puede cancelar igual sin llenar nada
 * ("Cancelar de todas formas"), pero lo motivamos suavemente a contarnos por qué.
 * El feedback se persiste en al_cancellation_feedback para análisis de churn.
 *
 * Diseño: sin dark patterns — no escondemos el botón de cancelar, no agregamos
 * fricción artificial, no ofrecemos descuentos agresivos. Solo escuchamos.
 */
import { useState } from "react";
import { Icon } from "../lib/Icon";
import type { CancelFeedback } from "../api/subscriptions";

interface CancellationSurveyModalProps {
  /** Estado actual de la suscripción (pending o active) — cambia el copy. */
  status: "pending" | "active";
  /** Fecha hasta la que mantiene acceso (solo si active). */
  renewsAt?: string | null;
  /** El coach confirma la cancelación con (o sin) feedback. */
  onConfirm: (feedback?: CancelFeedback) => void;
  onClose: () => void;
  /** True mientras se procesa la cancelación en backend. */
  cancelling: boolean;
}

const REASONS: { value: string; label: string; icon: string }[] = [
  { value: "too_expensive", label: "Es muy costoso para mí ahora", icon: "money" },
  { value: "using_other", label: "Estoy usando otra herramienta", icon: "grid" },
  { value: "not_useful", label: "No es lo que necesitaba", icon: "search" },
  { value: "missing_feature", label: "Le falta una función importante", icon: "sparkle" },
  { value: "pause_only", label: "Solo necesito una pausa, volveré", icon: "calendar" },
  { value: "tech_issues", label: "Tuve problemas técnicos", icon: "hammer" },
  { value: "other", label: "Otra razón", icon: "chat" },
];

export function CancellationSurveyModal({
  status,
  renewsAt,
  onConfirm,
  onClose,
  cancelling,
}: CancellationSurveyModalProps) {
  const [reason, setReason] = useState<string>("");
  const [competitorApp, setCompetitorApp] = useState("");
  const [comment, setComment] = useState("");
  const [wouldReturn, setWouldReturn] = useState<boolean | null>(null);

  const isPending = status === "pending";

  const handleConfirm = () => {
    // Feedback opcional: si no eligió razón, igual cancelamos (sin survey)
    const feedback: CancelFeedback | undefined = reason
      ? {
          reason,
          competitor_app: reason === "using_other" && competitorApp.trim() ? competitorApp.trim() : undefined,
          comment: comment.trim() || undefined,
          would_return: wouldReturn ?? undefined,
        }
      : undefined;
    onConfirm(feedback);
  };

  return (
    <div className="al-modal-backdrop" onClick={cancelling ? undefined : onClose}>
      <div
        className="al-modal al-cancel-survey"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="al-modal-hd">
          <div>
            <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: 20 }}>
              {isPending ? "Cancelar suscripción pendiente" : "Lamentamos que te vayas"}
            </h3>
            <div className="tiny muted" style={{ marginTop: 4 }}>
              {isPending
                ? "Como aún no completaste el pago, no se te cobrará nada."
                : renewsAt
                ? `Mantenés acceso completo hasta el ${new Date(renewsAt).toLocaleDateString("es-CO", { day: "numeric", month: "long" })}.`
                : "Mantenés acceso hasta el final de tu periodo pagado."}
            </div>
          </div>
          {!cancelling && (
            <button className="icon-btn" onClick={onClose} title="Cerrar">
              <Icon name="x" size={16} />
            </button>
          )}
        </div>

        <div className="al-cancel-survey-body">
          {!isPending && (
            <p className="tiny muted" style={{ lineHeight: 1.5, margin: "0 0 4px" }}>
              Tu opinión nos ayuda a mejorar AetherLite para los demás entrenadores.
              Contanos por qué te vas — es opcional, pero lo valoramos mucho.
            </p>
          )}

          {/* Razones */}
          <div className="al-cancel-reasons">
            {REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                className={"al-cancel-reason" + (reason === r.value ? " is-selected" : "")}
                onClick={() => setReason(reason === r.value ? "" : r.value)}
              >
                <span className="al-cancel-reason-radio">
                  {reason === r.value && <span className="al-cancel-reason-dot" />}
                </span>
                <Icon name={r.icon} size={15} />
                <span>{r.label}</span>
              </button>
            ))}
          </div>

          {/* Campo condicional: qué app usás */}
          {reason === "using_other" && (
            <div className="al-cancel-field">
              <label className="tiny" style={{ fontWeight: 600 }}>
                ¿Cuál herramienta? (opcional)
              </label>
              <input
                type="text"
                className="al-input"
                value={competitorApp}
                onChange={(e) => setCompetitorApp(e.target.value)}
                placeholder="Ej. Trainerize, TrueCoach, Excel…"
              />
            </div>
          )}

          {/* Comentario libre */}
          {reason && (
            <div className="al-cancel-field">
              <label className="tiny" style={{ fontWeight: 600 }}>
                {reason === "missing_feature"
                  ? "¿Qué función te hizo falta?"
                  : reason === "tech_issues"
                  ? "¿Qué problema tuviste?"
                  : "¿Algo más que quieras contarnos? (opcional)"}
              </label>
              <textarea
                className="al-input"
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tu comentario nos ayuda a mejorar…"
                style={{ resize: "vertical", minHeight: 64 }}
              />
            </div>
          )}

          {/* ¿Volverías? — solo si dio una razón */}
          {reason && reason !== "pause_only" && (
            <div className="al-cancel-field">
              <label className="tiny" style={{ fontWeight: 600 }}>
                Si solucionamos eso, ¿volverías a AetherLite?
              </label>
              <div className="al-cancel-return-opts">
                <button
                  type="button"
                  className={"al-cancel-return-btn" + (wouldReturn === true ? " is-selected" : "")}
                  onClick={() => setWouldReturn(wouldReturn === true ? null : true)}
                >
                  Sí, volvería
                </button>
                <button
                  type="button"
                  className={"al-cancel-return-btn" + (wouldReturn === false ? " is-selected" : "")}
                  onClick={() => setWouldReturn(wouldReturn === false ? null : false)}
                >
                  Probablemente no
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer: acciones — sin dark patterns, ambos botones claros */}
        <div className="al-cancel-survey-footer">
          <button
            className="btn btn-ghost"
            onClick={onClose}
            disabled={cancelling}
          >
            Mantener mi plan
          </button>
          <button
            className="btn al-cancel-confirm-btn"
            onClick={handleConfirm}
            disabled={cancelling}
          >
            {cancelling
              ? "Cancelando…"
              : reason
              ? "Enviar y cancelar"
              : "Cancelar de todas formas"}
          </button>
        </div>
      </div>
    </div>
  );
}
